import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Calendar, User, Building2, Stethoscope, FileText, DollarSign, Activity, AlertCircle, AlertTriangle, RefreshCw, CheckCircle2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Helper to display value or placeholder
const DisplayValue = ({ value, placeholder = "Not found in document", showWarning = false }) => {
  if (value === null || value === undefined || value === '') {
    return (
      <span className="text-gray-400 italic flex items-center gap-1">
        {showWarning && <AlertCircle className="h-3 w-3" />}
        {placeholder}
      </span>
    );
  }
  return <span>{value}</span>;
};

// Confidence badge component
const ConfidenceBadge = ({ confidence }) => {
  if (confidence === undefined || confidence === null) return null;
  
  const getColor = (conf) => {
    if (conf >= 0.8) return { bg: '#D1F4E0', text: '#166534' };
    if (conf >= 0.5) return { bg: '#FEF3C7', text: '#92400E' };
    return { bg: '#FEE2E2', text: '#991B1B' };
  };
  
  const { bg, text } = getColor(confidence);
  const percent = Math.round(confidence * 100);
  
  return (
    <span 
      className="text-xs px-2 py-0.5 rounded-full ml-2"
      style={{ backgroundColor: bg, color: text }}
    >
      {percent}% confidence
    </span>
  );
};

const RevampedChronologyTab = ({ results, onReprocess }) => {
  // Check if results are null/undefined
  if (!results) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium" style={{ color: '#64748B' }}>No chronology data available</p>
        <p className="text-sm mt-2" style={{ color: '#94a3b8' }}>
          Please process the documents to generate the chronology.
        </p>
      </div>
    );
  }
  
  // Check if fallback mode was used
  const isFallbackMode = results._fallback_mode === true;
  const fallbackMessage = results._fallback_message || 'Generated using fallback processing due to temporary AI service issue.';
  const needsReview = results._needs_review === true;
  const overallConfidence = results._overall_confidence;
  const validationFlags = results._validation_flags || [];
  const fieldConfidence = results._field_confidence || {};
  const billingValidated = results._billing_validated !== false;

  // Support both NEW and OLD schema formats
  // NEW schema fields
  const patientDemo = results.patient_demographics || {};
  const facilities = results.facilities || [];
  const diagnostics = results.diagnostics_imaging || [];
  const billingSummary = results.billing_summary || [];
  
  // OLD schema fields (fallback)
  const patientCaseInfo = results.patient_case_info || {};
  const providers = results.providers_and_facilities || [];
  const timeline = results.treatment_timeline || [];
  const oldBilling = results.billing_and_specials || {};
  const chronologyText = results.chronology_text || '';
  
  // Use OLD schema values as fallbacks for empty NEW schema fields
  const effectivePatientDemo = Object.keys(patientDemo).length > 0 ? patientDemo : {
    name: patientCaseInfo.patient_full_name,
    date_of_birth: patientCaseInfo.date_of_birth,
    date_of_accident: patientCaseInfo.date_of_injury
  };
  
  // Transform OLD providers to NEW facilities format if facilities is empty
  const effectiveFacilities = facilities.length > 0 ? facilities : providers.map(p => ({
    facility_name: p.facility_name,
    facility_type: p.facility_type,
    provider_name: p.provider_name,
    first_visit_date: timeline.find(t => t.facility_name === p.facility_name)?.start_date_of_service,
    visits: timeline.filter(t => t.facility_name === p.facility_name).map(t => ({
      date_of_service: t.start_date_of_service,
      diagnosis: t.diagnoses?.map(d => ({ description: d })) || [],
      treatment: t.treatments_rendered?.join('; '),
      notes: t.discharge_plan_or_recommendations
    }))
  }));
  
  // Transform OLD billing to NEW format if billing_summary is empty
  const effectiveBilling = billingSummary.length > 0 ? billingSummary : 
    (oldBilling.totals_by_provider || []).map(b => ({
      facility_name: b.provider_or_facility,
      total_billed: `$${(b.total_charges || 0).toFixed(2)}`,
      total_visits: (oldBilling.line_items || []).filter(l => l.provider_or_facility === b.provider_or_facility).length
    }));

  // Check if ALL data is empty (both schemas)
  const hasNoData = 
    Object.keys(effectivePatientDemo).filter(k => effectivePatientDemo[k]).length === 0 && 
    effectiveFacilities.length === 0 && 
    diagnostics.length === 0 && 
    effectiveBilling.length === 0 &&
    !chronologyText;

  if (hasNoData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium" style={{ color: '#64748B' }}>Summary data is empty</p>
        <p className="text-sm mt-2" style={{ color: '#94a3b8' }}>
          The processing completed but no data was extracted. Please try reprocessing the documents.
        </p>
      </div>
    );
  }
  
  // Use effective values for rendering
  const displayPatientDemo = effectivePatientDemo;
  const displayFacilities = effectiveFacilities;
  const displayBilling = effectiveBilling;

  return (
    <div className="space-y-8">
      {/* FALLBACK MODE BANNER */}
      {isFallbackMode && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-full bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-amber-800">Fallback Processing Used</h4>
                  {overallConfidence !== undefined && (
                    <ConfidenceBadge confidence={overallConfidence} />
                  )}
                </div>
                <p className="text-amber-700 text-sm mb-2">
                  {fallbackMessage}
                </p>
                
                {/* Validation flags */}
                {validationFlags.length > 0 && (
                  <div className="mt-3 p-2 bg-amber-100 rounded">
                    <p className="text-xs font-medium text-amber-800 mb-1">Extraction Notes:</p>
                    <ul className="text-xs text-amber-700 space-y-0.5">
                      {validationFlags.slice(0, 5).map((flag, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span>{flag.reason || flag}</span>
                        </li>
                      ))}
                      {validationFlags.length > 5 && (
                        <li className="text-amber-600">...and {validationFlags.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
                
                <p className="text-amber-600 text-xs mt-2">
                  For complete AI-powered extraction with full medical chronology, please reprocess when the service is available.
                </p>
              </div>
              {onReprocess && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReprocess}
                  className="border-amber-400 text-amber-700 hover:bg-amber-100"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* NEEDS REVIEW BANNER */}
      {needsReview && !isFallbackMode && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Review Recommended</p>
                <p className="text-sm text-yellow-700">Some extracted data has low confidence or validation issues. Please verify against source documents.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SECTION 1: PATIENT HEADER */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle style={{ color: '#245451' }}>
            Patient Information
            {fieldConfidence.patient_name !== undefined && (
              <ConfidenceBadge confidence={fieldConfidence.patient_name} />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4" style={{ color: '#84a2ac' }} />
                <span className="text-sm font-medium" style={{ color: '#64748B' }}>Patient Name</span>
              </div>
              <p className="text-lg font-semibold" style={{ color: displayPatientDemo.name ? '#245451' : '#9CA3AF' }}>
                <DisplayValue value={displayPatientDemo.name} placeholder="Not found in document" />
              </p>
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4" style={{ color: '#84a2ac' }} />
                <span className="text-sm font-medium" style={{ color: '#64748B' }}>Date of Birth</span>
              </div>
              <p className="text-lg font-semibold" style={{ color: '#245451' }}>
                {displayPatientDemo.date_of_birth || 'N/A'}
              </p>
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4" style={{ color: '#C49603' }} />
                <span className="text-sm font-medium" style={{ color: '#64748B' }}>Date of Accident</span>
              </div>
              <p className="text-lg font-semibold" style={{ color: '#C49603' }}>
                {displayPatientDemo.date_of_accident || 'N/A'}
              </p>
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4" style={{ color: '#84a2ac' }} />
                <span className="text-sm font-medium" style={{ color: '#64748B' }}>Accident Type</span>
              </div>
              <p className="text-lg font-semibold" style={{ color: '#245451' }}>
                {displayPatientDemo.accident_type || 'Not specified'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SECTION 2 & 3: FACILITY & PROVIDER OVERVIEW + CHRONOLOGY BREAKDOWN */}
      {displayFacilities.length > 0 ? (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>
            Treatment Chronology
          </h2>
          
          {displayFacilities.map((facility, facilityIndex) => {
            // Check if this is an imaging facility
            const isImagingFacility = (() => {
              const name = (facility.facility_name || '').toLowerCase();
              const type = (facility.facility_type || '').toLowerCase();
              const imagingKeywords = ['imaging', 'radiology', 'mri', 'ct scan', 'x-ray', 'xray', 'ultrasound', 'diagnostic'];
              return imagingKeywords.some(k => name.includes(k) || type.includes(k));
            })();
            
            // Check if this is a pharmacy facility
            const isPharmacyFacility = (() => {
              const name = (facility.facility_name || '').toLowerCase();
              const type = (facility.facility_type || '').toLowerCase();
              return name.includes('pharmacy') || type.includes('pharmacy');
            })();
            
            // Check if this is a hospital/ER facility
            const isHospitalFacility = (() => {
              const name = (facility.facility_name || '').toLowerCase();
              const type = (facility.facility_type || '').toLowerCase();
              const hospitalKeywords = ['hospital', 'emergency', 'er ', ' er', 'emergency room', 'medical center', 'trauma'];
              return hospitalKeywords.some(k => name.includes(k) || type.includes(k));
            })();
            
            // Get imaging studies associated with this hospital (if any)
            const hospitalImagingStudies = isHospitalFacility ? diagnostics.filter(d => {
              if (!d.facility_name) return false;
              const diagFacility = d.facility_name.toLowerCase();
              const facilityName = facility.facility_name.toLowerCase();
              // Match if diagnostic facility contains hospital name or vice versa
              return diagFacility.includes(facilityName.split(' ')[0]) || 
                     facilityName.includes(diagFacility.split(' ')[0]);
            }) : [];
            
            return (
            <Card key={facilityIndex} className="glass-card">
              {/* SECTION 2: Facility Header */}
              <CardHeader className="border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.2)', backgroundColor: 'rgba(36, 84, 81, 0.05)' }}>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: isImagingFacility ? 'rgba(132, 162, 172, 0.15)' : 'rgba(36, 84, 81, 0.15)' }}>
                    {isImagingFacility ? (
                      <Activity className="h-6 w-6" style={{ color: '#84a2ac' }} />
                    ) : (
                      <Building2 className="h-6 w-6" style={{ color: '#245451' }} />
                    )}
                  </div>
                  <div className="flex-1">
                    {/* Facility Name */}
                    <CardTitle className="text-xl mb-1" style={{ color: '#245451' }}>
                      {facility.facility_name || 'Facility name not documented'}
                    </CardTitle>
                    {isImagingFacility && (
                      <p className="text-sm mb-2" style={{ color: '#0369A1' }}>Diagnostic Imaging</p>
                    )}
                    
                    <div className="space-y-2 mt-3">
                      {/* Type - Use CONTROLLED FACILITY TYPE LIST */}
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold" style={{ color: '#245451' }}>Type:</span>
                        <span style={{ color: '#64748B' }}>
                          {facility.facility_type || (isImagingFacility ? 'Imaging Center' : 'Other (needs review)')}
                        </span>
                        {facility._needs_review && (
                          <span className="px-2 py-0.5 text-xs rounded" style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', color: '#854D0E' }}>
                            Needs Review
                          </span>
                        )}
                      </div>
                      
                      {/* Date of Services - show range for multiple visits */}
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold" style={{ color: '#245451' }}>Date of Services:</span>
                        <span style={{ color: '#64748B' }}>
                          {facility.first_visit_date || 'Not documented'}
                          {facility.last_visit_date && facility.last_visit_date !== facility.first_visit_date && 
                            ` – ${facility.last_visit_date}`
                          }
                        </span>
                      </div>
                      
                      {/* Number of Visits - with validation indicator */}
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold" style={{ color: '#245451' }}>Number of Visits:</span>
                        <span style={{ color: '#64748B' }}>
                          {facility.total_visits 
                            ? `${facility.total_visits} ${facility.total_visits === 1 ? 'Visit' : 'Visits'}`
                            : 'Not stated (needs review)'
                          }
                        </span>
                        {/* Show validation status */}
                        {facility._visit_count_validated === false && facility.total_visits && (
                          <span className="px-2 py-0.5 text-xs rounded" style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', color: '#854D0E' }}>
                            ⚠️ Unvalidated
                          </span>
                        )}
                      </div>
                      
                      {/* Treating Provider - shown for ALL facility types */}
                      {facility.treating_provider && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-semibold" style={{ color: '#245451' }}>
                            {isImagingFacility ? 'Radiologist:' : 'Treating Physician:'}
                          </span>
                          <span style={{ color: '#64748B' }}>{facility.treating_provider}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>

              {/* SECTION 3: Chronology Breakdown by DOS */}
              <CardContent className="pt-6">
                {/* SPECIAL HANDLING FOR IMAGING FACILITIES: Show all studies once, not per-visit */}
                {isImagingFacility ? (
                  <div className="space-y-6">
                    {/* Get ALL imaging studies for this facility from diagnostics array */}
                    {(() => {
                      const facilityImagingStudies = diagnostics.filter(d => 
                        d.facility_name && facility.facility_name && 
                        d.facility_name.toLowerCase().includes(facility.facility_name.toLowerCase().split(' ')[0])
                      );
                      
                      if (facilityImagingStudies.length === 0) {
                        return <p style={{ color: '#64748B' }}>No imaging studies documented</p>;
                      }
                      
                      return facilityImagingStudies.map((study, studyIndex) => (
                        <div key={studyIndex}>
                          {studyIndex > 0 && <Separator className="my-6" />}
                          
                          <div className="space-y-4">
                            {/* Study Type Header */}
                            <div className="flex items-center gap-2">
                              <Activity className="h-5 w-5" style={{ color: '#84a2ac' }} />
                              <h4 className="text-lg font-bold" style={{ color: '#245451' }}>
                                {study.study_type}
                              </h4>
                            </div>
                            
                            <div className="pl-7 space-y-4">
                              {/* Date of Study */}
                              {study.date_of_study && (
                                <div>
                                  <span className="font-semibold" style={{ color: '#245451' }}>Date: </span>
                                  <span style={{ color: '#64748B' }}>{study.date_of_study}</span>
                                </div>
                              )}
                              
                              {/* Findings */}
                              <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(196, 150, 3, 0.08)', border: '1px solid rgba(196, 150, 3, 0.2)' }}>
                                <h6 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#C49603' }}>
                                  <FileText className="h-4 w-4" />
                                  Findings:
                                </h6>
                                {study.impressions && study.impressions.length > 0 ? (
                                  <ul className="list-disc list-inside space-y-2">
                                    {(Array.isArray(study.impressions) ? study.impressions : [study.impressions]).map((finding, findingIdx) => (
                                      <li key={findingIdx} style={{ color: '#245451' }}>
                                        {finding}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p style={{ color: '#64748B', fontStyle: 'italic' }}>Impression not provided in report</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                ) : facility.visits && facility.visits.length > 0 ? (
                  <div className="space-y-6">
                    {facility.visits.map((visit, visitIndex) => (
                      <div key={visitIndex}>
                        {visitIndex > 0 && <Separator className="my-6" />}
                        
                        {/* ==================== PHARMACY-ONLY FORMAT ==================== */}
                        {/* For pharmacy facilities: only show Treatment Rendered (medications dispensed) */}
                        {isPharmacyFacility ? (
                          <div className="space-y-4">
                            {/* Date of Service */}
                            <div>
                              <h5 className="font-semibold mb-1" style={{ color: '#245451' }}>Date of Service:</h5>
                              <p style={{ color: '#64748B' }}>{visit.date_of_service || 'Not documented'}</p>
                            </div>

                            {/* Treatment Rendered (Medications) */}
                            <div>
                              <h5 className="font-semibold mb-1" style={{ color: '#245451' }}>Treatment Rendered:</h5>
                              {(() => {
                                // Check for treatment_rendered array (MUST be array)
                                if (Array.isArray(visit.treatment_rendered) && visit.treatment_rendered.length > 0) {
                                  return (
                                    <ul className="list-disc list-inside space-y-1">
                                      {visit.treatment_rendered.map((treatment, idx) => (
                                        <li key={idx} style={{ color: '#64748B' }}>{treatment}</li>
                                      ))}
                                    </ul>
                                  );
                                }
                                // If treatment_rendered is a string, display directly
                                if (typeof visit.treatment_rendered === 'string' && visit.treatment_rendered.trim()) {
                                  return <p style={{ color: '#64748B' }}>{visit.treatment_rendered}</p>;
                                }
                                // Fallback: check treatment_provided.medications (must be array)
                                if (Array.isArray(visit.treatment_provided?.medications) && visit.treatment_provided.medications.length > 0) {
                                  return (
                                    <ul className="list-disc list-inside space-y-1">
                                      {visit.treatment_provided.medications.map((med, idx) => (
                                        <li key={idx} style={{ color: '#64748B' }}>{med}</li>
                                      ))}
                                    </ul>
                                  );
                                }
                                return <p className="italic" style={{ color: '#64748B' }}>Medication details in pharmacy records</p>;
                              })()}
                            </div>
                          </div>
                        ) : (
                          /* ==================== STANDARD TREATMENT ENTRY FORMAT (NON-IMAGING) ==================== */
                          /* Per MEDICAL CHRONOLOGY — STANDARD TREATMENT ENTRY FORMAT specification */
                          <div className="space-y-4">
                            {/* Date of Service with Provider Name and Visit Type */}
                            <div>
                              <h5 className="font-semibold mb-1" style={{ color: '#245451' }}>Date of Service:</h5>
                              <p style={{ color: '#64748B' }}>
                                {visit.date_of_service || 'Not documented'}
                                {visit.visit_type && visit.visit_type !== 'Visit' && (
                                  <span className="ml-2 text-sm px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(36, 84, 81, 0.1)', color: '#245451' }}>
                                    {visit.visit_type}
                                  </span>
                                )}
                              </p>
                              {/* Needs Review Flag */}
                              {visit.needs_review && (
                                <p className="text-sm mt-1 px-2 py-1 rounded inline-block" style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', color: '#854D0E' }}>
                                  ⚠️ Needs Review - Limited clinical documentation
                                </p>
                              )}
                            </div>

                            {/* Chief Complaints */}
                            <div>
                              <h5 className="font-semibold mb-1" style={{ color: '#245451' }}>Chief Complaints:</h5>
                              {Array.isArray(visit.chief_complaints) && visit.chief_complaints.length > 0 ? (
                                <ul className="list-disc list-inside space-y-1">
                                  {visit.chief_complaints.map((complaint, idx) => (
                                    <li key={idx} style={{ color: '#64748B' }}>{complaint}</li>
                                  ))}
                                </ul>
                              ) : typeof visit.chief_complaints === 'string' && visit.chief_complaints.trim() ? (
                                <p style={{ color: '#64748B' }}>{visit.chief_complaints}</p>
                              ) : (
                                <p className="italic" style={{ color: '#64748B' }}>Not documented</p>
                              )}
                            </div>

                            {/* Assessment / Evaluation */}
                            <div>
                              <h5 className="font-semibold mb-1" style={{ color: '#245451' }}>Assessment / Evaluation:</h5>
                              {visit.assessment_evaluation ? (
                                <div style={{ color: '#64748B' }} className="space-y-2">
                                  {(typeof visit.assessment_evaluation === 'string' ? visit.assessment_evaluation.split('\n') : []).map((line, idx) => {
                                    // Handle section headers (text starting with **)
                                    if (line.startsWith('**') && line.endsWith('**')) {
                                      const headerText = line.replace(/\*\*/g, '');
                                      return (
                                        <p key={idx} className="font-semibold mt-3" style={{ color: '#245451' }}>
                                          {headerText}
                                        </p>
                                      );
                                    }
                                    // Handle bullet points
                                    if (line.startsWith('•')) {
                                      return (
                                        <p key={idx} className="ml-4">
                                          {line}
                                        </p>
                                      );
                                    }
                                    // Handle regular paragraphs (skip empty lines)
                                    if (line.trim()) {
                                      return (
                                        <p key={idx} className="mt-2">
                                          {line}
                                        </p>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              ) : (
                                <p className="italic" style={{ color: '#64748B' }}>Not documented</p>
                              )}
                            </div>

                            {/* Diagnoses / ICD-10 Codes and Injuries */}
                            <div>
                              <h5 className="font-semibold mb-2" style={{ color: '#245451' }}>Diagnoses / ICD-10 Codes and Injuries:</h5>
                              {Array.isArray(visit.diagnosis) && visit.diagnosis.length > 0 ? (
                                <ul className="list-disc list-inside space-y-1">
                                  {visit.diagnosis.map((dx, idx) => (
                                    <li key={idx} style={{ color: '#64748B' }}>
                                      <span className="font-mono font-semibold" style={{ color: '#854D0E' }}>{dx.code}</span>
                                      <span className="mx-2">–</span>
                                      <span>{dx.description}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : typeof visit.diagnosis === 'string' && visit.diagnosis.trim() ? (
                                <p style={{ color: '#64748B' }}>{visit.diagnosis}</p>
                              ) : (
                                <p className="italic" style={{ color: '#64748B' }}>Not documented</p>
                              )}
                            </div>

                            {/* Treatment Rendered */}
                            <div>
                              <h5 className="font-semibold mb-1" style={{ color: '#245451' }}>Treatment Rendered:</h5>
                              {(() => {
                                // First check for new treatment_rendered array (MUST be array, not string)
                                if (Array.isArray(visit.treatment_rendered) && visit.treatment_rendered.length > 0) {
                                  return (
                                    <ul className="list-disc list-inside space-y-1">
                                      {visit.treatment_rendered.map((treatment, idx) => (
                                        <li key={idx} style={{ color: '#64748B' }}>{treatment}</li>
                                      ))}
                                    </ul>
                                  );
                                }
                                
                                // If treatment_rendered is a string, display it directly
                                if (typeof visit.treatment_rendered === 'string' && visit.treatment_rendered.trim()) {
                                  return <p style={{ color: '#64748B' }}>{visit.treatment_rendered}</p>;
                                }
                                
                                // Fallback: collect from treatment_provided object
                                const treatments = [];
                                if (visit.treatment_provided) {
                                  // Handle chiropractic_adjustments (can be string or boolean)
                                  if (visit.treatment_provided.chiropractic_adjustments) {
                                    const val = visit.treatment_provided.chiropractic_adjustments;
                                    treatments.push(typeof val === 'string' && val !== 'Applied.' ? val : 'Chiropractic adjustments');
                                  }
                                  // Handle manual_therapy (can be string or boolean)
                                  if (visit.treatment_provided.manual_therapy) {
                                    const val = visit.treatment_provided.manual_therapy;
                                    treatments.push(typeof val === 'string' && val !== 'Massage therapy provided.' ? val : 'Manual therapy / Massage');
                                  }
                                  // Handle physical_therapy (can be string or boolean)
                                  if (visit.treatment_provided.physical_therapy && visit.treatment_provided.physical_therapy !== 'Not specified.') {
                                    const val = visit.treatment_provided.physical_therapy;
                                    treatments.push(typeof val === 'string' ? val : 'Physical therapy');
                                  }
                                  // Handle medications (must be array)
                                  if (Array.isArray(visit.treatment_provided.medications) && visit.treatment_provided.medications.length > 0) {
                                    treatments.push(...visit.treatment_provided.medications);
                                  }
                                  // Handle injections (must be array)
                                  if (Array.isArray(visit.treatment_provided.injections) && visit.treatment_provided.injections.length > 0) {
                                    treatments.push(...visit.treatment_provided.injections);
                                  }
                                  // Handle treatments (must be array)
                                  if (Array.isArray(visit.treatment_provided.treatments) && visit.treatment_provided.treatments.length > 0) {
                                    treatments.push(...visit.treatment_provided.treatments);
                                  }
                                  // Handle other (must be array)
                                  if (Array.isArray(visit.treatment_provided.other) && visit.treatment_provided.other.length > 0) {
                                    treatments.push(...visit.treatment_provided.other);
                                  }
                                }
                                
                                return treatments.length > 0 ? (
                                  <ul className="list-disc list-inside space-y-1">
                                    {treatments.map((treatment, idx) => (
                                      <li key={idx} style={{ color: '#64748B' }}>{treatment}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="italic" style={{ color: '#64748B' }}>Not documented</p>
                                );
                              })()}
                            </div>

                            {/* Recommendations / Plan */}
                            <div>
                              <h5 className="font-semibold mb-1" style={{ color: '#245451' }}>Recommendations / Plan:</h5>
                              {visit.recommendations && (Array.isArray(visit.recommendations) ? visit.recommendations.length > 0 : visit.recommendations) ? (
                                Array.isArray(visit.recommendations) ? (
                                  <ul className="list-disc list-inside space-y-1">
                                    {visit.recommendations.map((rec, idx) => (
                                      <li key={idx} style={{ color: '#64748B' }}>{rec}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p style={{ color: '#64748B' }}>{visit.recommendations}</p>
                                )
                              ) : (
                                <p className="italic" style={{ color: '#64748B' }}>Not documented</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#64748B' }}>No visit details available</p>
                )}
                
                {/* Hospital/ER Imaging Studies Section */}
                {isHospitalFacility && hospitalImagingStudies.length > 0 && (
                  <div className="mt-6 pt-6 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#245451' }}>
                      <Activity className="h-5 w-5" style={{ color: '#84a2ac' }} />
                      Imaging Studies Performed at this Facility
                    </h4>
                    <div className="space-y-3 pl-7">
                      {hospitalImagingStudies.map((study, studyIdx) => (
                        <div key={studyIdx} className="flex items-start gap-2">
                          <span className="text-sm font-medium" style={{ color: '#245451' }}>•</span>
                          <div>
                            <span className="font-medium" style={{ color: '#245451' }}>{study.study_type}</span>
                            {study.date_of_study && (
                              <span className="text-sm ml-2" style={{ color: '#64748B' }}>({study.date_of_study})</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      ) : (
        <Card className="glass-card">
          <CardContent className="py-12 text-center">
            <p style={{ color: '#64748B' }}>No treatment facilities recorded</p>
          </CardContent>
        </Card>
      )}

      {/* SECTION 4: DIAGNOSTICS / IMAGING */}
      {/* Filter out diagnostics that are already shown in imaging facility cards */}
      {(() => {
        // Get list of imaging facility names to exclude their diagnostics
        const imagingFacilityNames = displayFacilities
          .filter(f => {
            const name = (f.facility_name || '').toLowerCase();
            const type = (f.facility_type || '').toLowerCase();
            const imagingKeywords = ['imaging', 'radiology', 'mri', 'ct scan', 'x-ray', 'xray', 'ultrasound', 'diagnostic'];
            return imagingKeywords.some(k => name.includes(k) || type.includes(k));
          })
          .map(f => f.facility_name.toLowerCase().split(' ')[0]);
        
        // Filter diagnostics to exclude those from imaging facilities already displayed
        const filteredDiagnostics = diagnostics.filter(d => {
          if (!d.facility_name) return true;
          const diagFacilityName = d.facility_name.toLowerCase();
          // If this diagnostic belongs to an imaging facility that's shown, exclude it
          return !imagingFacilityNames.some(ifn => diagFacilityName.includes(ifn));
        });
        
        return filteredDiagnostics.length > 0 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>
            Diagnostics & Imaging
          </h2>
          
          <Card className="glass-card">
            <CardContent className="pt-6">
              <div className="space-y-6">
                {filteredDiagnostics.map((imaging, idx) => (
                  <div key={idx}>
                    {idx > 0 && <Separator className="my-6" />}
                    
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(132, 162, 172, 0.15)' }}>
                          <Activity className="h-5 w-5" style={{ color: '#84a2ac' }} />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold mb-2" style={{ color: '#245451' }}>
                            {imaging.study_type}
                          </h4>
                          <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium" style={{ color: '#64748B' }}>Facility: </span>
                              <span style={{ color: '#245451' }}>{imaging.facility_name}</span>
                            </div>
                            {imaging.imaging_doctor && (
                              <div>
                                <span className="font-medium" style={{ color: '#64748B' }}>Radiologist: </span>
                                <span style={{ color: '#245451' }}>{imaging.imaging_doctor}</span>
                              </div>
                            )}
                            {imaging.referring_doctor && (
                              <div>
                                <span className="font-medium" style={{ color: '#64748B' }}>Referring Doctor: </span>
                                <span style={{ color: '#245451' }}>{imaging.referring_doctor}</span>
                              </div>
                            )}
                            <div>
                              <span className="font-medium" style={{ color: '#64748B' }}>Date of Service: </span>
                              <span style={{ color: '#245451' }}>{imaging.date_of_service}</span>
                            </div>
                          </div>
                          
                          {imaging.impressions && (
                            <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: 'rgba(196, 150, 3, 0.05)' }}>
                              <p className="font-semibold mb-2" style={{ color: '#C49603' }}>Impressions:</p>
                              {Array.isArray(imaging.impressions) ? (
                                <ul className="list-disc list-inside space-y-1">
                                  {imaging.impressions.map((imp, impIdx) => (
                                    <li key={impIdx} style={{ color: '#64748B' }}>{imp}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p style={{ color: '#64748B' }}>{imaging.impressions}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        );
      })()}

      {/* SECTION 5: BILLING SUMMARY TABLE */}
      {displayBilling.length > 0 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>
            Billing Summary
          </h2>
          
          <Card className="glass-card">
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: 'rgba(36, 84, 81, 0.2)' }}>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: '#245451' }}>Facility Name</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: '#245451' }}>Date(s) of Service</th>
                      <th className="text-center py-3 px-4 font-semibold" style={{ color: '#245451' }}>Total Visits</th>
                      <th className="text-right py-3 px-4 font-semibold" style={{ color: '#245451' }}>Total Billed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayBilling.map((bill, idx) => {
                      // Format the billed amount with commas
                      const formatCurrency = (value) => {
                        if (!value) return 'N/A';
                        const numValue = parseFloat(value.replace(/[$,]/g, '')) || 0;
                        return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      };
                      
                      return (
                        <tr key={idx} className="border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <td className="py-3 px-4 font-medium" style={{ color: '#245451' }}>
                            {bill.facility_name}
                          </td>
                          <td className="py-3 px-4" style={{ color: '#64748B' }}>
                            {bill.dates_of_service}
                          </td>
                          <td className="text-center py-3 px-4" style={{ color: '#64748B' }}>
                            {bill.total_visits}
                          </td>
                          <td className="text-right py-3 px-4 font-bold" style={{ color: '#C49603' }}>
                            {formatCurrency(bill.total_billed)}
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* Total Row */}
                    <tr className="border-t-2" style={{ borderColor: 'rgba(36, 84, 81, 0.3)' }}>
                      <td colSpan="2" className="py-4 px-4 font-bold text-right" style={{ color: '#245451' }}>
                        GRAND TOTAL:
                      </td>
                      <td className="text-center py-4 px-4 font-bold" style={{ color: '#245451' }}>
                        {displayBilling.reduce((sum, b) => sum + (b.total_visits || 0), 0)}
                      </td>
                      <td className="text-right py-4 px-4 font-bold text-xl" style={{ color: '#C49603' }}>
                        {(() => {
                          const total = displayBilling.reduce((sum, b) => {
                            const amount = parseFloat(b.total_billed.replace(/[$,]/g, '')) || 0;
                            return sum + amount;
                          }, 0);
                          return `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        })()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RevampedChronologyTab;
